import React, { memo } from 'react';

const keyframeCSS = `
  @media (max-width: 768px) {
    .box-stage { padding: 2px !important; }
    .box-stage-floor { padding: 6px 4px 10px !important; }
    .box-grid {
      gap: 6px !important;
      max-width: 100% !important;
      grid-template-columns: repeat(4, 1fr) !important;
    }
    .box-region-label { font-size: 0.38rem !important; }
    .box-number-plate { font-size: 0.7rem !important; padding: 2px 6px !important; }
    .box-prize-value { font-size: 0.42rem !important; }
  }
  @media (max-width: 400px) {
    .box-grid { gap: 4px !important; }
  }

  @keyframes boxShine {
    0% { left: -100%; }
    50%, 100% { left: 200%; }
  }
  @keyframes playerGlow {
    0%, 100% { box-shadow: 0 0 15px rgba(212,175,55,0.5), 0 0 30px rgba(212,175,55,0.2); }
    50% { box-shadow: 0 0 30px rgba(212,175,55,0.8), 0 0 60px rgba(212,175,55,0.4), 0 0 90px rgba(212,175,55,0.15); }
  }
  @keyframes playerGlowRing {
    0%, 100% { opacity: 0.4; transform: scale(1); }
    50% { opacity: 0.8; transform: scale(1.05); }
  }
  @keyframes lidOpenAnim {
    0% { transform: rotateX(0deg) translateY(0); opacity: 1; }
    40% { transform: rotateX(-80deg) translateY(-4px); opacity: 0.8; }
    100% { transform: rotateX(-120deg) translateY(-8px); opacity: 0.3; }
  }
  @keyframes innerGlowPulse {
    0%, 100% { opacity: 0.3; }
    50% { opacity: 0.7; }
  }
  @keyframes prizeFloat {
    0%, 100% { transform: translateY(0px); }
    50% { transform: translateY(-4px); }
  }
  @keyframes boxAppear {
    0% { transform: scale(0.3) rotateY(90deg); opacity: 0; }
    60% { transform: scale(1.05) rotateY(-5deg); opacity: 1; }
    100% { transform: scale(1) rotateY(0deg); opacity: 1; }
  }
  @keyframes selectionPulse {
    0%, 100% { box-shadow: 0 0 0 0 rgba(212,175,55,0); }
    50% { box-shadow: 0 0 0 6px rgba(212,175,55,0.15); }
  }
  @keyframes boxHoverGlow {
    0%, 100% { box-shadow: 0 8px 25px rgba(0,0,0,0.5); }
    50% { box-shadow: 0 8px 30px rgba(212,175,55,0.15), 0 12px 35px rgba(0,0,0,0.5); }
  }
  @keyframes openedSmoke {
    0% { opacity: 0.5; transform: scaleY(1); }
    100% { opacity: 0; transform: scaleY(2) translateY(-10px); }
  }
`;

function formatEuro(value) {
  if (typeof value !== 'number') return String(value);
  return value.toLocaleString('it-IT', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: value < 1 ? 2 : 0,
    maximumFractionDigits: 2,
  });
}

export default function BoxGrid({ boxes = [], playerBoxNumber, onBoxClick, disabled = false, selectionMode = false, showAllPrizes = false }) {
  const sorted = [...boxes].sort((a, b) => a.box_number - b.box_number);

  return (
    <div className="box-stage" style={{
      width: '100%',
      maxWidth: '560px',
      margin: '0 auto',
      padding: '4px',
    }}>
      <style>{keyframeCSS}</style>

      {selectionMode && (
        <div style={{
          textAlign: 'center',
          fontFamily: 'var(--font-display)',
          fontSize: '1.2rem',
          fontWeight: 700,
          color: '#D4AF37',
          letterSpacing: '0.12em',
          textTransform: 'uppercase',
          marginBottom: '20px',
          textShadow: '0 0 30px rgba(212,175,55,0.4), 0 0 60px rgba(212,175,55,0.15)',
          animation: 'selectionPulse 2s ease-in-out infinite',
        }}>
          Seleziona la tua scatola
        </div>
      )}

      <div className="box-stage-floor" style={{
        background: `
          linear-gradient(180deg, transparent 0%, rgba(212,175,55,0.02) 40%, rgba(212,175,55,0.05) 100%),
          radial-gradient(ellipse 80% 40% at 50% 100%, rgba(212,175,55,0.06) 0%, transparent 70%)
        `,
        borderRadius: '16px',
        padding: '20px 16px 24px',
        position: 'relative',
        overflow: 'hidden',
        border: '1px solid rgba(212,175,55,0.06)',
      }}>
        {/* Stage floor reflection line */}
        <div style={{
          position: 'absolute',
          bottom: 0,
          left: '5%',
          right: '5%',
          height: '1px',
          background: 'linear-gradient(90deg, transparent, rgba(212,175,55,0.25), transparent)',
        }} />
        {/* Stage side lights */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '2px',
          height: '100%',
          background: 'linear-gradient(180deg, rgba(212,175,55,0.15), transparent 30%, transparent 70%, rgba(212,175,55,0.15))',
        }} />
        <div style={{
          position: 'absolute',
          top: 0,
          right: 0,
          width: '2px',
          height: '100%',
          background: 'linear-gradient(180deg, rgba(212,175,55,0.15), transparent 30%, transparent 70%, rgba(212,175,55,0.15))',
        }} />

        <div className="box-grid" style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(5, 1fr)',
          gap: '10px',
          maxWidth: '560px',
          margin: '0 auto',
        }}>
          {sorted.map((box, index) => {
            const isPlayerBox = box.is_player_box || box.box_number === playerBoxNumber;
            const clickable = selectionMode
              ? !box.is_opened
              : !box.is_opened && !isPlayerBox && !disabled;

            return (
              <BoxItem
                key={box.box_number}
                box={box}
                index={index}
                isPlayerBox={isPlayerBox}
                clickable={clickable}
                selectionMode={selectionMode}
                onBoxClick={onBoxClick}
                showPrize={showAllPrizes}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
}

const BoxItem = memo(function BoxItem({ box, index, isPlayerBox, clickable, selectionMode, onBoxClick, showPrize }) {
  const isOpened = box.is_opened;
  const prizeVal = box.prize_value;
  const isHighPrize = prizeVal !== undefined && prizeVal >= 10000;
  const isLongPrize = prizeVal !== undefined && prizeVal >= 10000;

  const wrapperStyle = {
    perspective: '800px',
    cursor: clickable ? 'pointer' : 'default',
    position: 'relative',
    animation: !isOpened ? `boxAppear 0.5s ease-out ${index * 0.03}s both` : 'none',
  };

  const box3dStyle = {
    position: 'relative',
    width: '100%',
    aspectRatio: '3 / 3.8',
    transformStyle: 'preserve-3d',
  };

  // Box body - rich blue with metallic feel
  const bodyGradient = isOpened
    ? 'linear-gradient(180deg, #0d1b2a 0%, #0a1520 50%, #060e15 100%)'
    : isPlayerBox
      ? 'linear-gradient(180deg, #D4AF37 0%, #B8960C 30%, #8B6914 70%, #6B4F0E 100%)'
      : 'linear-gradient(180deg, #2196F3 0%, #1976D2 25%, #1565C0 50%, #0D47A1 100%)';

  const bodyStyle = {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: '76%',
    background: bodyGradient,
    borderRadius: '3px 3px 6px 6px',
    border: isPlayerBox && !isOpened
      ? '2px solid rgba(240,208,96,0.7)'
      : isOpened ? '1px solid rgba(30,50,80,0.3)' : '1px solid rgba(33,150,243,0.4)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '2px',
    overflow: 'hidden',
    boxShadow: isOpened
      ? 'inset 0 4px 15px rgba(0,0,0,0.7), 0 2px 4px rgba(0,0,0,0.3)'
      : isPlayerBox
        ? '0 6px 20px rgba(212,175,55,0.3), 0 2px 8px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.2)'
        : '0 6px 20px rgba(0,0,0,0.5), 0 2px 8px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.1)',
    opacity: isOpened ? 0.45 : 1,
    animation: isPlayerBox && !isOpened ? 'playerGlow 2.5s ease-in-out infinite' : 'none',
    transition: 'opacity 0.5s ease, transform 0.3s ease',
  };

  // Side face simulation (3D depth)
  const sideStyle = {
    position: 'absolute',
    bottom: 0,
    right: '-3px',
    width: '3px',
    height: '76%',
    background: isOpened
      ? 'rgba(5,10,20,0.5)'
      : isPlayerBox
        ? 'linear-gradient(180deg, #8B6914, #5a3d0a)'
        : 'linear-gradient(180deg, #0D47A1, #062a64)',
    borderRadius: '0 3px 3px 0',
    opacity: isOpened ? 0.3 : 0.7,
  };

  // Lid with better 3D
  const lidStyle = {
    position: 'absolute',
    top: 0,
    left: '-2px',
    right: '-2px',
    height: '30%',
    background: isOpened
      ? 'linear-gradient(180deg, #0d1b2a 0%, #0a1520 100%)'
      : isPlayerBox
        ? 'linear-gradient(180deg, #F0D060 0%, #D4AF37 50%, #B8960C 100%)'
        : 'linear-gradient(180deg, #42A5F5 0%, #2196F3 50%, #1976D2 100%)',
    borderRadius: '5px 5px 2px 2px',
    border: isPlayerBox && !isOpened
      ? '2px solid rgba(240,208,96,0.6)'
      : isOpened ? '1px solid rgba(30,50,80,0.2)' : '1px solid rgba(66,165,245,0.5)',
    transformOrigin: 'top center',
    animation: isOpened ? 'lidOpenAnim 0.8s ease-out forwards' : 'none',
    boxShadow: isOpened
      ? 'none'
      : '0 3px 10px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.2)',
    zIndex: 2,
    transition: 'all 0.3s ease',
  };

  // Lid handle/ridge
  const lidRidgeStyle = {
    position: 'absolute',
    top: '50%',
    left: '20%',
    right: '20%',
    height: '2px',
    background: isPlayerBox
      ? 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)'
      : 'linear-gradient(90deg, transparent, rgba(255,255,255,0.15), transparent)',
    transform: 'translateY(-50%)',
    borderRadius: '1px',
    zIndex: 3,
  };

  // Gold number plate with embossed look
  const numberPlateStyle = {
    background: isPlayerBox
      ? 'linear-gradient(135deg, #1565C0 0%, #2196F3 50%, #1565C0 100%)'
      : 'linear-gradient(135deg, #C8962A 0%, #F0D060 30%, #D4AF37 60%, #C8962A 100%)',
    color: isPlayerBox ? '#fff' : '#2a1800',
    fontFamily: 'var(--font-display)',
    fontSize: 'clamp(0.8rem, 1.8vw, 1.1rem)',
    fontWeight: 700,
    padding: '2px 10px',
    borderRadius: '3px',
    textAlign: 'center',
    lineHeight: 1.3,
    boxShadow: '0 2px 6px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.3), inset 0 -1px 0 rgba(0,0,0,0.2)',
    position: 'relative',
    zIndex: 3,
    minWidth: '28px',
    letterSpacing: '0.02em',
    textShadow: isPlayerBox ? '0 1px 2px rgba(0,0,0,0.3)' : 'none',
  };

  // Region label
  const regionStyle = {
    fontSize: '0.55rem',
    fontFamily: 'var(--font-display)',
    fontWeight: 600,
    color: 'rgba(255,255,255,0.85)',
    textAlign: 'center',
    lineHeight: 1.2,
    letterSpacing: '0.03em',
    textTransform: 'uppercase',
    padding: '1px 4px',
    maxWidth: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    zIndex: 3,
    position: 'relative',
    textShadow: '0 1px 4px rgba(0,0,0,0.6)',
  };

  // Shine overlay - sweeping light effect
  const shineStyle = {
    position: 'absolute',
    top: 0,
    left: '-100%',
    width: '60%',
    height: '100%',
    background: isPlayerBox
      ? 'linear-gradient(90deg, transparent, rgba(255,255,255,0.12), transparent)'
      : 'linear-gradient(90deg, transparent, rgba(255,255,255,0.08), transparent)',
    animation: !isOpened ? 'boxShine 5s ease-in-out infinite' : 'none',
    animationDelay: `${index * 0.25}s`,
    zIndex: 4,
    pointerEvents: 'none',
  };

  // Prize reveal inside opened box
  const prizeRevealStyle = {
    position: 'absolute',
    bottom: '12%',
    left: '2px',
    right: '2px',
    fontFamily: 'var(--font-mono)',
    fontSize: isLongPrize ? '0.55rem' : '0.7rem',
    fontWeight: 700,
    color: isHighPrize ? '#F0D060' : '#5dade2',
    textAlign: 'center',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    zIndex: 5,
    textShadow: isHighPrize
      ? '0 0 12px rgba(212,175,55,0.7), 0 0 25px rgba(212,175,55,0.3)'
      : '0 0 10px rgba(52,152,219,0.6), 0 0 20px rgba(52,152,219,0.3)',
    animation: 'prizeFloat 3s ease-in-out infinite',
    lineHeight: 1,
  };

  // Inner glow for opened box
  const innerGlowStyle = {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: '78%',
    background: isHighPrize
      ? 'radial-gradient(ellipse at center, rgba(212,175,55,0.2) 0%, transparent 70%)'
      : 'radial-gradient(ellipse at center, rgba(52,152,219,0.12) 0%, transparent 70%)',
    animation: 'innerGlowPulse 2s ease-in-out infinite',
    pointerEvents: 'none',
    zIndex: 1,
    borderRadius: '4px',
  };

  // Player glow ring
  const playerGlowStyle = {
    position: 'absolute',
    inset: '-5px',
    borderRadius: '10px',
    background: 'transparent',
    border: '2px solid rgba(212,175,55,0.35)',
    animation: 'playerGlowRing 2s ease-in-out infinite',
    pointerEvents: 'none',
    zIndex: 0,
    boxShadow: '0 0 15px rgba(212,175,55,0.15), inset 0 0 15px rgba(212,175,55,0.05)',
  };

  return (
    <div
      style={wrapperStyle}
      onClick={() => clickable && onBoxClick?.(box.box_number)}
      onMouseEnter={clickable ? (e) => {
        e.currentTarget.style.transform = 'scale(1.12) translateY(-8px)';
        e.currentTarget.style.filter = 'brightness(1.15)';
        e.currentTarget.style.zIndex = '10';
      } : undefined}
      onMouseLeave={clickable ? (e) => {
        e.currentTarget.style.transform = '';
        e.currentTarget.style.filter = '';
        e.currentTarget.style.zIndex = '';
      } : undefined}
    >
      <div style={box3dStyle}>
        {/* Lid */}
        <div style={lidStyle}>
          {!isOpened && <div style={lidRidgeStyle} />}
        </div>

        {/* Box body */}
        <div style={bodyStyle}>
          {/* Number plate */}
          {!isOpened && (
            <div className="box-number-plate" style={numberPlateStyle}>
              {box.box_number}
            </div>
          )}

          {/* Region label */}
          {!isOpened && box.region_name && (
            <div className="box-region-label" style={{
              ...regionStyle,
              ...(showPrize ? { fontSize: '0.42rem', padding: 0 } : {}),
            }}>{box.region_name}</div>
          )}

          {/* Prize value visible to organizer */}
          {!isOpened && showPrize && box.prize_value !== undefined && (
            <div className="box-prize-value" style={{
              fontSize: '0.48rem',
              fontFamily: 'var(--font-mono)',
              fontWeight: 700,
              color: '#F0D060',
              textAlign: 'center',
              lineHeight: 1,
              textShadow: '0 1px 4px rgba(0,0,0,0.6)',
              zIndex: 3,
              position: 'relative',
            }}>
              {box.prize_label && (box.prize_value === 0 || box.prize_value === undefined)
                ? box.prize_label
                : formatEuro(box.prize_value)}
            </div>
          )}

          {/* Shine effect */}
          {!isOpened && <div style={shineStyle} />}
        </div>

        {/* 3D side depth */}
        {!isOpened && <div style={sideStyle} />}

        {/* Prize reveal for opened boxes */}
        {isOpened && (box.prize_value !== undefined || box.prize_label) && (
          <div className="box-prize-value" style={prizeRevealStyle}>
            {box.prize_label && (box.prize_value === 0 || box.prize_value === undefined)
              ? box.prize_label
              : formatEuro(box.prize_value)}
          </div>
        )}

        {/* Inner glow for opened boxes */}
        {isOpened && <div style={innerGlowStyle} />}

        {/* Player box special glow ring */}
        {isPlayerBox && !isOpened && <div style={playerGlowStyle} />}
      </div>
    </div>
  );
});
